import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Coins, Save, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { useApi, currencies } from "./useApi";

export function CurrencySettings({ currency, userCurrency, setUserCurrency, updateCurrency, refetchCurrency, t }) {
  var [savingCurrency, setSavingCurrency] = useState(false);

  async function handleSaveCurrency() {
    setSavingCurrency(true);
    try {
      var success = await updateCurrency(userCurrency);
      if (success) {
        toast.success(t("settings.currencyUpdated"));
        refetchCurrency();
      } else {
        toast.error(t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setSavingCurrency(false);
    }
  }

  return (
    <Card className="bento-card" data-testid="currency-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Coins className="w-5 h-5" />{t("settings.currency")}</CardTitle>
        <CardDescription>{t("settings.currencyDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select value={userCurrency} onValueChange={setUserCurrency}>
          <SelectTrigger className="w-full" data-testid="currency-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {currencies.map(function(curr) {
              return <SelectItem key={curr.code} value={curr.code}>{curr.name}</SelectItem>;
            })}
          </SelectContent>
        </Select>
        <Button onClick={handleSaveCurrency} disabled={savingCurrency || userCurrency === currency} data-testid="currency-save-btn">
          <Save className="w-4 h-4 mr-2" />{savingCurrency ? t("settings.saving") : t("settings.saveCurrency")}
        </Button>
      </CardContent>
    </Card>
  );
}

export function DashboardSettingsCard({ token, t }) {
  var [dashboardSettings, setDashboardSettings] = useState({ dashboard_title: "", dashboard_subtitle: "" });
  var [savingDashboard, setSavingDashboard] = useState(false);
  var { fetchJson, putJson } = useApi(token);

  useEffect(function() {
    fetchJson("/api/settings/dashboard").then(setDashboardSettings).catch(function() {});
  }, []);

  async function handleSaveDashboardSettings() {
    setSavingDashboard(true);
    try {
      var response = await putJson("/api/settings/dashboard", dashboardSettings);
      if (response.ok) {
        toast.success(t("settings.dashboardUpdated"));
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setSavingDashboard(false);
    }
  }

  return (
    <Card className="bento-card" data-testid="dashboard-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><LayoutDashboard className="w-5 h-5" />{t("settings.dashboardCustomization")}</CardTitle>
        <CardDescription>{t("settings.dashboardCustomizationDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="form-label">{t("settings.dashboardTitle")}</Label>
          <Input value={dashboardSettings.dashboard_title} onChange={function(e) { setDashboardSettings({...dashboardSettings, dashboard_title: e.target.value}); }} placeholder={t("dashboard.title")} data-testid="dashboard-title-input" />
          <p className="text-xs text-muted-foreground mt-1">{t("settings.dashboardTitleHint")}</p>
        </div>
        <div>
          <Label className="form-label">{t("settings.dashboardSubtitle")}</Label>
          <Input value={dashboardSettings.dashboard_subtitle} onChange={function(e) { setDashboardSettings({...dashboardSettings, dashboard_subtitle: e.target.value}); }} placeholder={t("dashboard.subtitle")} data-testid="dashboard-subtitle-input" />
          <p className="text-xs text-muted-foreground mt-1">{t("settings.dashboardSubtitleHint")}</p>
        </div>
        <Button onClick={handleSaveDashboardSettings} disabled={savingDashboard} data-testid="dashboard-save-btn">
          <Save className="w-4 h-4 mr-2" />{savingDashboard ? t("settings.saving") : t("settings.saveDashboard")}
        </Button>
      </CardContent>
    </Card>
  );
}
